import React, { useEffect, useMemo } from "react";
import StyledModal from "../../components/CustomModal";
import { Form, FormInstance, Input, Row, Col } from "antd";
import { useAppSelector } from "../../utils/hooks/redux";
import { FooterButtons, FormWrapper, StyledButton } from "../../components/styles";
import { selectCategories } from "../../redux/features/category/categorySlice";

const languageOptions = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "pt", label: "Portuguese" },
  { code: "ar", label: "Arabic" },
];

type ModalProps = {
  isModalOpen: boolean;
  handleSave: (values: any) => void;
  onCancel: () => void;
  form: FormInstance<any>;
  hasAddPermission: boolean,
  hasEditPermission: boolean,
  hasViewPermission: boolean
};

const CreateEditModal: React.FC<ModalProps> = ({ isModalOpen, handleSave, onCancel, form, hasAddPermission, hasEditPermission, hasViewPermission }) => {
  const { loading } = useAppSelector(selectCategories);
  const canSaveChanges = useMemo(() => hasAddPermission || hasEditPermission, [hasAddPermission, hasEditPermission]);

  const parseTranslations = (value: any) => {
    if (!value) return {};
    if (typeof value === "string") {
      try {
        return JSON.parse(value) || {};
      } catch {
        return {};
      }
    }
    return value;
  };

  useEffect(() => {
    if (isModalOpen) {
      const record = form.getFieldsValue(true);
      form.setFieldsValue({
        translationKey: record?.translationKey || undefined,
        nameTranslations: parseTranslations(record?.nameTranslations),
      });
    }
  }, [isModalOpen]);

  return (
    <StyledModal
    draggable={true}
      title={form.getFieldValue("id") ? `${canSaveChanges ? "Edit" : "View"} Category` : `${canSaveChanges ? "Add" : "View"} Category`}
      open={isModalOpen}
      closeIcon={null}
      footer={
        <FooterButtons>
          <StyledButton onClick={onCancel} disabled={loading}>
            Cancel
          </StyledButton>
          {canSaveChanges && <StyledButton
            type="primary"
            onClick={async () => {
              try {
                const values = await form.validateFields();
                handleSave(values);
              } catch (error) {
                console.error("Validation failed:", error);
              }
            }}
            loading={loading}
          >
            Save
          </StyledButton>}
        </FooterButtons>
      }
      centered
      onCancel={() => !loading && onCancel()}
    >
      <FormWrapper form={form} layout="vertical">
        <Form.Item label="Category Name" name="name" rules={[{ required: true, message: "Please enter category name" }]}>
          <Input size="large" disabled={loading || !canSaveChanges} />
        </Form.Item>
        <Form.Item label="Translation Key" name="translationKey" tooltip="Optional i18n key for this category">
          <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g. category.electronics" />
        </Form.Item>
        <div style={{ marginBottom: 8, fontWeight: 600 }}>Localized Names</div>
        <Row gutter={[12, 12]}>
          {languageOptions.map((lang) => (
            <Col span={12} key={lang.code}>
              <Form.Item name={["nameTranslations", lang.code]} label={lang.label}>
                <Input
                  size="large"
                  disabled={loading || !canSaveChanges}
                  placeholder={`Enter ${lang.label} name`}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </FormWrapper>
    </StyledModal>
  );
};

export default CreateEditModal;
